import { useCallback, useReducer, useRef, useState } from 'react';
import { useStoreState } from 'easy-peasy';
import { companySearchService as defaultService } from '../services/companySearchService';

// ─── Constants ────────────────────────────────────────────────────────────────

const DEBOUNCE_MS = 300;

// ─── Default Scheduler (injected for testability per ADR-003) ─────────────────

// eslint-disable-next-line no-undef
export const defaultScheduler = {
  // eslint-disable-next-line no-undef
  setTimeout: (fn, ms) => setTimeout(fn, ms),
  // eslint-disable-next-line no-undef
  clearTimeout: id => clearTimeout(id),
};

// ─── State Machine ────────────────────────────────────────────────────────────

const INITIAL_STATE = { status: 'idle', companies: [], error: null };

function reducer(state, action) {
  switch (action.type) {
    case 'SEARCH_START':
      return { ...state, status: 'loading', error: null };
    case 'SEARCH_SUCCESS':
      return { status: 'success', companies: action.payload, error: null };
    case 'SEARCH_ERROR':
      return { ...state, status: 'error', error: action.payload };
    case 'RESET':
      return INITIAL_STATE;
    default:
      return { ...state };
  }
}

// ─── Pure Logic ───────────────────────────────────────────────────────────────

export const deriveSearchState = state => ({
  isLoading: state.status === 'loading',
  hasResults: state.status === 'success' && state.companies.length > 0,
  isEmpty: state.status === 'success' && state.companies.length === 0,
});

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Orchestrates company typeahead search.
 * Debounces keystrokes and guards against race conditions via requestId ref.
 *
 * `service` and `scheduler` are injected for testability — pass mocks in tests.
 */
export function useCompanySearch(
  service = defaultService,
  scheduler = defaultScheduler,
) {
  const { userId } = useStoreState(state => state.auth);
  const [query, setQueryValue] = useState('');
  const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

  const requestId = useRef(0);
  const debounceRef = useRef(null);

  const executeSearch = useCallback(
    async (name, currentId) => {
      dispatch({ type: 'SEARCH_START' });
      try {
        const companies = await service.searchCompanies({ userId, name });
        if (currentId !== requestId.current) return;
        dispatch({ type: 'SEARCH_SUCCESS', payload: companies });
      } catch (err) {
        if (currentId !== requestId.current) return;
        dispatch({ type: 'SEARCH_ERROR', payload: err });
      }
    },
    [service, userId],
  );

  const setQuery = useCallback(
    value => {
      setQueryValue(value);
      scheduler.clearTimeout(debounceRef.current);

      if (!value) {
        dispatch({ type: 'RESET' });
        return;
      }

      // eslint-disable-next-line no-plusplus
      const currentId = ++requestId.current;
      debounceRef.current = scheduler.setTimeout(
        () => executeSearch(value, currentId),
        DEBOUNCE_MS,
      );
    },
    [scheduler, executeSearch],
  );

  const reset = useCallback(() => {
    scheduler.clearTimeout(debounceRef.current);
    setQueryValue('');
    dispatch({ type: 'RESET' });
  }, [scheduler]);

  const derived = deriveSearchState(state);

  return {
    query,
    status: state.status,
    companies: state.companies,
    error: state.error,
    isLoading: derived.isLoading,
    hasResults: derived.hasResults,
    isEmpty: derived.isEmpty,
    setQuery,
    reset,
  };
}
